import { Component, OnInit, Input, inject, signal } from '@angular/core';
import { settledCache$ } from '../../shared/utils/cache-ready';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { filter, take } from 'rxjs';

import { Baa } from '../../objects/baa';
import { BaaService } from '../../service/entity-services/baa.service';

@Component({
  selector: 'app-baa-connections',
  templateUrl: './baa-connections.component.html',
  styleUrls: ['./baa-connections.component.css'],
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatChipsModule, MatProgressSpinnerModule]
})
export class BaaConnectionsComponent implements OnInit {
  private baaService = inject(BaaService);
  private router = inject(Router);

  baaObjectArray = signal<Baa[]>([]);
  waiting = signal<boolean>(true);

  @Input() selectedBaaList: any[] = [];

  ngOnInit(): void {
    this.hydrateBaaData();
  }

  private hydrateBaaData(): void {
    if (!this.selectedBaaList || this.selectedBaaList.length === 0) {
      console.log('[BaaConnections:Diag] No BAA list provided, showing empty state');
      this.waiting.set(false);
      return;
    }

    this.waiting.set(true);

    // Extract BAA identifiers, dedup
    const baaIds = Array.from(new Set(
      this.selectedBaaList.map(b =>
        typeof b === 'object' && b?.baaNum ? b.baaNum :
        typeof b === 'object' && b?.baaId ? b.baaId : b
      ).filter(id => !!id)
    ));

    console.log('[BaaConnections:Diag] Looking up BAA IDs:', baaIds);

    if (baaIds.length === 0) {
      this.waiting.set(false);
      return;
    }

    // Wait for the BAA cache to FINISH streaming, then look up directly.
    // `filter(arr => arr.length > 0)` resolved on page 1, so any linked BAA on a
    // later page was dropped by the .find() below and silently vanished from the
    // tab — the list looked complete while being wrong.
    settledCache$(this.baaService.allBaas$).subscribe({
      next: (allBaas) => {
        console.log('[BaaConnections:Diag] BAA cache size:', allBaas.length);
        const matched = baaIds
          .map(id => allBaas.find(baa => baa.baaId === id || baa.baaNum === id))
          .filter((baa): baa is Baa => baa !== null && baa !== undefined);
        console.log('[BaaConnections:Diag] Matched BAAs:', matched.length, 'of', baaIds.length, 'requested');
        this.baaObjectArray.set(matched);
        this.waiting.set(false);
      },
      error: (err) => {
        console.error('Error loading BAA connections:', err);
        this.waiting.set(false);
      }
    });
  }

  navigateToBaa(baa: any): void {
    const num = baa.baaNum || baa.baaId;
    if (num) {
      window.open(`/securehome/baa-record/${num}`, '_blank');
    }
  }

  formatDate(date: string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  }

  extractPartyName(field: any): string {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field.name || field.friendlyName || field.vendorData?.name || '';
  }
}
